import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { money } from "@/lib/format";
import { ShieldAlert, Plus, Trash2, Loader2, Wand2, X } from "lucide-react";

const CATS = ["Site", "Design", "Procurement", "Weather", "Labour", "Financial", "Regulatory", "General"];
const cellColor = (score) => score >= 15 ? "bg-red-500/25 border-red-500/40" : score >= 8 ? "bg-amber-500/25 border-amber-500/40" : score >= 4 ? "bg-yellow-500/15 border-yellow-500/30" : "bg-emerald-500/15 border-emerald-500/30";

export default function RiskTab({ projectId }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", category: "Site", probability: 3, impact: 3, cost_impact: 0, mitigation: "", status: "open" });

  const { data: risks = [], isLoading } = useQuery({ queryKey: ["risks", projectId], queryFn: async () => (await api.get(`/projects/${projectId}/risks`)).data });
  const { data: summary } = useQuery({ queryKey: ["risk-summary", projectId], queryFn: async () => (await api.get(`/projects/${projectId}/risks/summary`)).data });

  const refresh = () => { qc.invalidateQueries({ queryKey: ["risks", projectId] }); qc.invalidateQueries({ queryKey: ["risk-summary", projectId] }); };

  const add = async () => {
    try { await api.post(`/projects/${projectId}/risks`, form); refresh(); setOpen(false); setForm({ ...form, title: "", cost_impact: 0, mitigation: "" }); toast.success("Risk added"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (id) => { try { await api.delete(`/risks/${id}`); refresh(); } catch (e) { toast.error(apiErr(e)); } };
  const apply = async () => {
    try { const { data } = await api.post(`/projects/${projectId}/risks/apply-contingency`); refresh();
      qc.invalidateQueries({ queryKey: ["boq", projectId] }); qc.invalidateQueries({ queryKey: ["projects"] });
      toast.success(`Applied ${data.applied_contingency_pct}% contingency → ${money(data.grand_total, 0)}`); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (isLoading) return <div className="grid place-items-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  // matrix cells: prob 5..1 (rows), impact 1..5 (cols)
  const cellRisks = (p, i) => risks.filter((r) => r.probability === p && r.impact === i);

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold flex items-center gap-2"><ShieldAlert className="h-4 w-4 text-primary" /> Risk Register</h3>
          <button data-testid="add-risk-btn" onClick={() => setOpen(true)} className="inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground px-4 py-2 font-bold hover:bg-orange-600 transition-colors"><Plus className="h-4 w-4" />Add Risk</button>
        </div>

        {risks.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border py-14 text-center text-muted-foreground">No risks logged yet.</div>
        ) : (
          <div className="rounded-xl border border-border bg-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[640px]" data-testid="risk-table">
                <thead><tr className="text-left text-[10px] font-mono uppercase tracking-wider text-muted-foreground border-b border-border/60">
                  <th className="px-4 py-2">Risk</th><th className="px-2 py-2 text-center">P×I</th>
                  <th className="px-2 py-2 text-right">Cost Impact</th><th className="px-2 py-2 text-right">EMV</th>
                  <th className="px-2 py-2 text-center">Status</th><th className="px-2 py-2"></th>
                </tr></thead>
                <tbody>
                  {risks.map((r) => (
                    <tr key={r.id} className="border-b border-border/40" data-testid={`risk-row-${r.id}`}>
                      <td className="px-4 py-2.5"><div className="font-medium">{r.title}</div><div className="text-xs text-muted-foreground">{r.category}{r.mitigation ? ` · ${r.mitigation}` : ""}</div></td>
                      <td className="px-2 py-2.5 text-center"><span className={`inline-block px-2 py-0.5 rounded border font-mono text-xs ${cellColor(r.score)}`}>{r.score}</span></td>
                      <td className="px-2 py-2.5 text-right font-mono tabular-nums">{money(r.cost_impact, 0)}</td>
                      <td className="px-2 py-2.5 text-right font-mono font-semibold tabular-nums text-primary">{money(r.emv, 0)}</td>
                      <td className="px-2 py-2.5 text-center"><span className="text-[10px] font-mono uppercase capitalize text-muted-foreground">{r.status}</span></td>
                      <td className="px-2 py-2.5"><button data-testid={`delete-risk-${r.id}`} onClick={() => del(r.id)} className="h-7 w-7 grid place-items-center rounded text-muted-foreground hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Matrix */}
        <div className="rounded-xl border border-border bg-card p-5" data-testid="risk-matrix">
          <h3 className="font-semibold mb-4">Probability × Impact Matrix</h3>
          <div className="flex gap-2">
            <div className="flex flex-col justify-between py-1 text-[10px] font-mono text-muted-foreground"><span className="rotate-0">Prob →</span></div>
            <div className="flex-1">
              <div className="grid grid-cols-5 gap-1.5">
                {[5, 4, 3, 2, 1].map((p) => [1, 2, 3, 4, 5].map((i) => {
                  const rs = cellRisks(p, i);
                  return (
                    <div key={`${p}-${i}`} className={`aspect-square rounded border grid place-items-center text-xs ${cellColor(p * i)}`} title={`P${p} × I${i}`}>
                      {rs.length > 0 && <span className="font-mono font-bold">{rs.length}</span>}
                    </div>
                  );
                }))}
              </div>
              <div className="text-center text-[10px] font-mono text-muted-foreground mt-1.5">Impact →</div>
            </div>
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="space-y-4">
        <div className="rounded-xl border border-border bg-card p-5" data-testid="risk-summary-card">
          <h3 className="font-semibold mb-4">Contingency Advisor</h3>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Open risks</span><span className="font-mono">{summary?.open_count ?? 0}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Total EMV</span><span className="font-mono text-primary font-semibold">{money(summary?.emv_total, 0)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">BOQ base</span><span className="font-mono">{money(summary?.base_subtotal, 0)}</span></div>
            <div className="pt-3 mt-1 border-t border-border">
              <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">Suggested Contingency</div>
              <div className="text-3xl font-mono font-bold text-primary tabular-nums" data-testid="suggested-contingency">{summary?.suggested_contingency_pct ?? 0}%</div>
            </div>
          </div>
          <button data-testid="apply-contingency-btn" onClick={apply} disabled={!summary?.emv_total}
            className="w-full mt-4 inline-flex items-center justify-center gap-2 rounded-lg bg-primary text-primary-foreground py-2.5 font-bold hover:bg-orange-600 transition-colors disabled:opacity-50">
            <Wand2 className="h-4 w-4" /> Apply to BOQ
          </button>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setOpen(false)}>
          <div className="w-full max-w-md rounded-xl border border-border bg-popover p-6 animate-fade-up" onClick={(e) => e.stopPropagation()} data-testid="add-risk-modal">
            <div className="flex items-center justify-between mb-4"><h2 className="text-lg font-bold">Add Risk</h2><button onClick={() => setOpen(false)} className="h-8 w-8 grid place-items-center rounded-lg hover:bg-secondary"><X className="h-4 w-4" /></button></div>
            <div className="space-y-3">
              <input data-testid="risk-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Risk title" className="w-full rounded-lg border border-input bg-card px-3 py-2.5 outline-none focus:border-primary" />
              <div className="grid grid-cols-2 gap-3">
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="rounded-lg border border-input bg-card px-3 py-2.5 outline-none focus:border-primary">
                  {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <input data-testid="risk-cost-input" type="number" value={form.cost_impact} onChange={(e) => setForm({ ...form, cost_impact: Number(e.target.value) })} placeholder="Cost impact $" className="rounded-lg border border-input bg-card px-3 py-2.5 outline-none focus:border-primary font-mono" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <label className="text-sm">Probability<select data-testid="risk-prob-select" value={form.probability} onChange={(e) => setForm({ ...form, probability: Number(e.target.value) })} className="w-full mt-1 rounded-lg border border-input bg-card px-3 py-2 outline-none focus:border-primary">{[1,2,3,4,5].map(n=><option key={n} value={n}>{n}</option>)}</select></label>
                <label className="text-sm">Impact<select data-testid="risk-impact-select" value={form.impact} onChange={(e) => setForm({ ...form, impact: Number(e.target.value) })} className="w-full mt-1 rounded-lg border border-input bg-card px-3 py-2 outline-none focus:border-primary">{[1,2,3,4,5].map(n=><option key={n} value={n}>{n}</option>)}</select></label>
              </div>
              <input value={form.mitigation} onChange={(e) => setForm({ ...form, mitigation: e.target.value })} placeholder="Mitigation (optional)" className="w-full rounded-lg border border-input bg-card px-3 py-2.5 outline-none focus:border-primary" />
              <button data-testid="submit-risk-btn" onClick={add} disabled={!form.title} className="w-full rounded-lg bg-primary text-primary-foreground py-2.5 font-bold hover:bg-orange-600 transition-colors disabled:opacity-60">Add risk</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
